import React, { useState, useRef, useEffect } from "react";
import { GoogleGenAI, Type } from "@google/genai";
import { Video, Upload, Loader2, Scissors, Scan, CheckCircle2, AlertCircle, Key } from "lucide-react";
import { cn } from "../utils";
import { SectionPillHeader } from "./SectionPillHeader";

interface ShotDetectionResult {
  start_time: string;
  end_time: string;
  shot_code: string;
  description: string;
}

declare global {
  interface Window {
    aistudio: {
      hasSelectedApiKey: () => Promise<boolean>;
      openSelectKey: () => Promise<void>;
    };
  }
}

export function VideoAnalysis() {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ShotDetectionResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>("");
  const [hasUserKey, setHasUserKey] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const checkKey = async () => {
      if (window.aistudio) {
        const hasKey = await window.aistudio.hasSelectedApiKey();
        setHasUserKey(hasKey);
      }
    };
    checkKey();
  }, []);

  const handleSelectKey = async () => {
    if (window.aistudio) {
      await window.aistudio.openSelectKey();
      setHasUserKey(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type.includes("video") || selectedFile.name.endsWith(".mov")) {
        setFile(selectedFile);
        setResults([]);
        setError(null);
      } else {
        setError("Please upload a valid video file (.mov, .mp4, etc.)");
      }
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(",")[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const analyzeVideo = async () => {
    if (!file) return;

    // Pre-check for API key if we've already hit a cap or if no key is connected
    if (window.aistudio) {
      const hasKey = await window.aistudio.hasSelectedApiKey();
      if (!hasKey) {
        setError("A personal API key is required to bypass the spending cap. Please connect your key first.");
        await handleSelectKey();
        return;
      }
    }

    setIsProcessing(true);
    setError(null);
    setProgress("Preparing video for analysis...");

    try {
      const base64Data = await fileToBase64(file);
      
      // Create a new instance right before the call to ensure we use the latest key
      const apiKey = process.env.API_KEY || process.env.GEMINI_API_KEY || "";
      const ai = new GoogleGenAI({ apiKey });
      
      setProgress("Analyzing video content (this may take a minute)...");

      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: [
          {
            parts: [
              {
                inlineData: {
                  mimeType: file.type || "video/quicktime",
                  data: base64Data,
                },
              },
              {
                text: `Analyze this video for camera cuts and shot codes. 
                1. Identify every timestamp where a camera cut or scene change occurs.
                2. Look at the on-screen burn-ins (usually at the bottom or top corners) to extract the shot code (e.g., VFX_010, SH_020) for each identified shot.
                3. Provide a brief description of the action in each shot.
                
                Return the results as a JSON array of objects with these keys: "start_time", "end_time", "shot_code", "description".
                Use the format HH:MM:SS for timestamps.`,
              },
            ],
          },
        ],
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                start_time: { type: Type.STRING },
                end_time: { type: Type.STRING },
                shot_code: { type: Type.STRING },
                description: { type: Type.STRING },
              },
              required: ["start_time", "end_time", "shot_code", "description"],
            },
          },
        },
      });

      const text = response.text;
      if (text) {
        const parsedResults = JSON.parse(text);
        setResults(parsedResults);
        setProgress("Analysis complete!");
      } else {
        throw new Error("No response from Gemini");
      }
    } catch (err: any) {
      console.error("Video analysis error:", err);
      
      let errorMessage = "An error occurred during analysis.";
      let isQuotaError = false;
      const errorDetail = err.message || (typeof err === 'object' ? JSON.stringify(err) : String(err));
      
      // Robust error detection
      if (errorDetail.includes("spending cap") || errorDetail.includes("429") || errorDetail.includes("RESOURCE_EXHAUSTED")) {
        isQuotaError = true;
        errorMessage = "The API spending cap has been reached. Video analysis is a high-resource task that requires a Google Cloud project with an active billing account. Please ensure your connected API key is from a project with billing enabled.";
      } else {
        errorMessage = errorDetail;
      }
      
      if (isQuotaError) {
        setHasUserKey(false); // Reset state to prompt for a new key if the current one hit a cap
      }
      
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm shrink-0 mb-6">
        <SectionPillHeader
          icon={Scan}
          title="Video Analysis"
          description="Automatically detect and describe shots from video files using AI"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={handleSelectKey}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
              hasUserKey 
                ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800" 
                : "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
            )}
          >
            <Key className="w-3.5 h-3.5" />
            {hasUserKey ? "API Key Connected" : "Connect API Key"}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">

        <div className="p-8">
          {!file ? (
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-12 bg-slate-50/50 dark:bg-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer relative group">
              <input
                type="file"
                accept="video/*,.mov"
                onChange={handleFileChange}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
              <div className="w-16 h-16 bg-white dark:bg-slate-900 rounded-full shadow-sm border border-slate-200 dark:border-slate-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Upload className="w-8 h-8 text-indigo-600" />
              </div>
              <p className="text-lg font-medium text-slate-900 dark:text-slate-100">Click or drag to upload video</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Supports .mov, .mp4, .avi up to 20MB</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="aspect-video bg-black rounded-xl overflow-hidden shadow-lg border border-slate-800 relative group">
                    <video
                      ref={videoRef}
                      src={URL.createObjectURL(file)}
                      controls
                      className="w-full h-full"
                    />
                    <button 
                      onClick={() => setFile(null)}
                      className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <AlertCircle className="w-5 h-5 rotate-45" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                        <Video className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate max-w-[200px]">{file.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      onClick={analyzeVideo}
                      disabled={isProcessing}
                      className={cn(
                        "flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all shadow-sm",
                        isProcessing 
                          ? "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed" 
                          : "bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95"
                      )}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Scissors className="w-4 h-4" />
                          Run Cut Detection
                        </>
                      )}
                    </button>
                  </div>
                  {progress && (
                    <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium animate-pulse">
                      <Scan className="w-4 h-4" />
                      {progress}
                    </div>
                  )}
                  {error && (
                    <div className="flex flex-col gap-3 p-4 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 rounded-lg border border-red-100 dark:border-red-900/60 text-sm">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span className="font-bold">Error</span>
                      </div>
                      <p>{error}</p>
                      {error.includes("spending cap") && (
                        <div className="flex flex-col gap-2 mt-1">
                          <button
                            onClick={handleSelectKey}
                            className="w-fit px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition-colors"
                          >
                            Connect Personal API Key
                          </button>
                          <a 
                            href="https://ai.google.dev/gemini-api/docs/billing" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-red-500 dark:text-red-300 underline hover:text-red-600 dark:hover:text-red-200"
                          >
                            Learn about Gemini API billing
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      Detected Shots
                    </h3>
                    <span className="text-xs font-medium px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full">
                      {results.length} shots found
                    </span>
                  </div>

                  <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-sm h-[400px] overflow-y-auto">
                    {results.length > 0 ? (
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">Time</th>
                            <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">Shot Code</th>
                            <th className="px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {results.map((shot, idx) => (
                            <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/60 transition-colors group">
                              <td className="px-4 py-3 text-sm font-mono text-indigo-600 whitespace-nowrap">
                                {shot.start_time} - {shot.end_time}
                              </td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 rounded text-xs font-bold font-brandon border border-indigo-100 dark:border-indigo-900/50">
                                  {shot.shot_code}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                                {shot.description}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-center p-8 text-slate-400 dark:text-slate-500">
                        <Scan className="w-12 h-12 mb-4 opacity-20" />
                        <p className="text-sm">No shots detected yet.<br />Upload a video and run analysis to see results.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-6 flex gap-4">
        <div className="w-12 h-12 bg-white dark:bg-slate-900 rounded-full flex items-center justify-center shrink-0 shadow-sm">
          <Scan className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h4 className="font-bold text-indigo-900 dark:text-indigo-200">How it works</h4>
          <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-1 leading-relaxed">
            This tool uses Gemini 1.5 Flash to visually analyze your video. It identifies scene changes by understanding the content of the frames, and uses OCR (Optical Character Recognition) to extract shot codes from on-screen burn-ins. For best results, ensure the shot codes are clearly visible in the corners of the frame.
          </p>
        </div>
      </div>
    </div>
  );
}
